import { FC } from 'react';
import { Col, Row } from 'react-bootstrap';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  AsyncControlSelect,
  Checkbox,
  ControlSelect,
  Field,
  Textarea
} from '../../common/components/Form';
import { PopUp } from '../../common/components/PopUp';
import { useDeceased } from './useDeceased';
import { DeceasedFormSkeleton } from './DeceasedFormSkeleton';
import {
  FormCard,
  SectionTitle,
  ActionSection,
  PrimaryActionButton,
  SecondaryActionButton,
  FindButton,
  CheckboxWrapper,
  BabyAgeInfo,
  BabyAgeLabel,
  BabyAgeValue
} from './styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faIdCard, faHome } from '@fortawesome/free-solid-svg-icons';

type DeceasedFormProps = {
  deceasedId?: number;
};

export const DeceasedForm: FC<DeceasedFormProps> = (props): JSX.Element => {
  const { deceasedId } = props;
  const navigate = useNavigate();
  const {
    fields,
    methods,
    message,
    options,
    loading,
    onSubmit,
    togglePopup,
    handleFindFromUnis,
    loadRuianOptions,
    loadInitialRuianOption,
    isEditMode,
    babyAge
  } = useDeceased({ deceasedId });

  const isUnknown = methods.watch('is_unknown');

  if (loading && isEditMode && !methods.formState.isDirty) {
    return <DeceasedFormSkeleton />;
  }

  const formContent = (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <SectionTitle>
          <FontAwesomeIcon icon={faUser} />
          Osobní údaje
        </SectionTitle>
        {/* Riadok 1: Jméno, Příjmení, Checkbox */}
        <Row>
          <Col lg={4} md={12}>
            <Field {...fields.first_name} />
          </Col>
          <Col lg={4} md={12}>
            <Field {...fields.last_name} />
          </Col>
          <Col lg={4} md={12}>
            <CheckboxWrapper>
              <Checkbox id="is_unknown" name="is_unknown" label="Neznámá osoba" />
            </CheckboxWrapper>
          </Col>
        </Row>

        {/* Riadok 2: Tituly */}
        <Row>
          <Col lg={4} md={12}>
            <Field {...fields.title_before} />
          </Col>
          <Col lg={4} md={12}>
            <Field {...fields.title_after} />
          </Col>
        </Row>

        <Row>
          <Col lg={4} md={12}>
            <ControlSelect {...fields.gender} options={options.genders} autoTransform />
          </Col>
          <Col lg={4} md={12}>
            <Field {...fields.age_years} />
          </Col>
          {babyAge && (
            <Col lg={4} md={12}>
              <BabyAgeInfo>
                <BabyAgeLabel>Věk novorozence:</BabyAgeLabel>
                <BabyAgeValue>{babyAge}</BabyAgeValue>
              </BabyAgeInfo>
            </Col>
          )}
        </Row>

        {/* Riadok 3: Datum narození, Datum úmrtí */}
        <Row>
          <Col lg={4} md={12}>
            <Field {...fields.date_of_birth} />
          </Col>
          <Col lg={4} md={12}>
            <Field {...fields.date_of_death} />
          </Col>
        </Row>

        <SectionTitle>
          <FontAwesomeIcon icon={faIdCard} />
          Identifikační údaje
        </SectionTitle>

        {/* Riadok 4: RČ, Číslo pojistky */}
        <Row>
          <Col lg={4} md={12}>
            <Field {...fields.birth_id} disabled={isUnknown} />
          </Col>
          <Col lg={4} md={12}>
            <Field {...fields.insurance_number} disabled={isUnknown} />
          </Col>
          <Col lg={2} md={12}>
            {!isUnknown && (
              <FindButton type="button" onClick={handleFindFromUnis} disabled={isUnknown}>
                Synchronizovat s MPI
              </FindButton>
            )}
          </Col>
        </Row>

        {/* Riadok 5: Pojišťovna */}
        <Row>
          <Col lg={4} md={12}>
            <ControlSelect
              {...fields.insurance_company}
              options={options.insurances}
              autoTransform
            />
          </Col>
        </Row>

        <SectionTitle>
          <FontAwesomeIcon icon={faHome} />
          Adresa a kontakt
        </SectionTitle>

        <Row>
          <Col lg={6} md={12}>
            <AsyncControlSelect
              {...fields.residence_ruian_address}
              loadOptions={loadRuianOptions}
              loadInitialOption={loadInitialRuianOption}
            />
          </Col>
          <Col lg={6} md={12}>
            <Field {...fields.contact_address} />
          </Col>
        </Row>

        <Row>
          <Col lg={6} md={12}>

          </Col>
          <Col lg={6} md={12}>
            <AsyncControlSelect
              {...fields.contact_ruian_address}
              loadOptions={loadRuianOptions}
              loadInitialOption={loadInitialRuianOption}
            />
          </Col>
        </Row>

        {isUnknown && (
          <Row>
            <Col lg={12}>
              <Textarea {...fields.note} rows={5} />
            </Col>
          </Row>
        )}

        {/* Akčné tlačítka */}
        <ActionSection>
          <SecondaryActionButton type="button" onClick={() => navigate(-1)}>
            Zpět
          </SecondaryActionButton>
          <PrimaryActionButton type="submit">Uložit</PrimaryActionButton>
        </ActionSection>
      </form>
    </FormProvider>
  );

  return (
    <>
      {message && <PopUp type={message.type} message={message.message} toggle={togglePopup} />}
      {deceasedId ? formContent : <FormCard>{formContent}</FormCard>}
    </>
  );
};
